import { Config } from 'config/envConfig';
import React, { useEffect, useState } from 'react';
import { Table, Tooltip, Button } from 'antd';
import { CustomTable } from '@quyva/shared-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getUrl } from './pdfDisplay/utils';
import { CRAWL_STATUS_META } from '../enums';
import axios from 'axios';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { NoDataView } from './noDataView';

export const CrawlHistoryTable = ({
    repoId,
    repository,
    onSelectRow,
    selectedRowKey,
    setInitialRow,
    onStateChange,
}) => {
    const [crawlHistory, setCrawlHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    const getCrawlHistory = async () => {
        onStateChange?.('loading');
        try {
            const response = await axios.get(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT
                    }/crawl/crawl_timestamps?repo_id=${encodeURIComponent(
                        repoId
                    )}`
                )
            );

            const history = response.data;
            setCrawlHistory(history);
            onStateChange?.(Array.isArray(history) && history.length > 0 ? 'data' : 'empty');
            if (history.length > 0 && setInitialRow) {
                setInitialRow({
                    key: 0,
                    url: history[0].url,
                    md_file_name: history[0].md_file_name,
                    timestamp: history[0].last_crawled_at,
                    crawlStatus: history[0].crawl_status,
                    statusMessage: history[0].status_message,
                    totalWebpages: history[0].total_webpages,
                    successfullPages: history[0].successfull_pages,
                    imagesDetected: history[0].images_detected,
                    markdownSizeKb: history[0].markdown_size_kb,
                    documentsProcessed: history[0].successful_documents,
                    chunksGenerated: history[0].chunks_generated,
                });
            }
        } catch (error) {
            console.error('Error fetching crawl history:', error);
            setErrorMessage('Failed to fetch crawl history.');
            setCrawlHistory([]);
            onStateChange?.('error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getCrawlHistory();
    }, [repoId]);

    const INTERNET_CRAWL_COLUMNS = [
        {
            key: 'pagesSummary',
            title: 'Webpages Crawled',
            render: (_, record) => {
                const total = record?.totalWebpages;
                const success = record?.successfullPages;
                if (total === undefined && success === undefined) {
                    return '—';
                }
                const safeTotal = total ?? 0;
                const safeSuccess = success ?? 0;
                return (
                    <Tooltip
                        title={`Total webpages: ${safeTotal}, Successfull webpages: ${safeSuccess}`}
                    >
                        <span>{`${safeSuccess}/${safeTotal}`}</span>
                    </Tooltip>
                );
            },
        },
        { key: 'imagesDetected', title: 'Images Processed' },
        { key: 'chunksGenerated', title: 'Chunks Generated' },
        { key: 'documentsProcessed', title: 'Attachments Processed' },
        { key: 'markdownSizeKb', title: 'Markdown Size (KB)' },
    ];


    const columns = [
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            ellipsis:true,
            render: (url) => (
                <div className='flex items-center gap-2 min-w-0'>
                    <a
                        href={url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-texts-tertiary underline break-words min-w-0'
                        onClick={(e) => e.stopPropagation()}
                    >
                        {url}
                    </a>
                </div>
            ),
        },
        {
            title: 'Last Crawled',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (text) =>
                text ? new Date(text).toLocaleString() : 'Not Available',
        },
        {
            title: 'Crawl Status',
            dataIndex: 'crawlStatus',
            key: 'crawlStatus',
            render: (status) => {
                const { color, icon, tooltip } =
                    CRAWL_STATUS_META[status] || CRAWL_STATUS_META.DEFAULT;

                return (
                    <Tooltip title={tooltip}>
                        <span
                            className={`font-medium flex items-center gap-2 ${color}`}
                        >
                            {icon && <FontAwesomeIcon icon={icon} className="text-xs" />}
                            {status || '—'}
                        </span>
                    </Tooltip>
                );
            },
        },

        ...INTERNET_CRAWL_COLUMNS.map(({ key, title, render }) => ({
            title: key === 'markdownSizeKb' ? 'Size (KB)' : title,
            key,
            align: 'center',
            render: render || ((_, record) => {
                const v = record?.[key];
                if (v === undefined || v === null) {
                    return '—';
                }
                if (key === 'markdownSizeKb' && typeof v === 'number') {
                    return v.toFixed(2);
                }
                return v;
            }),
        })),
    ];

    const data = (crawlHistory || []).map((entry, index) => ({
        key: index,
        url: entry.url,
        md_file_name: entry.md_file_name,
        timestamp: entry.last_crawled_at,
        crawlStatus: entry.crawl_status,
        statusMessage: entry.status_message,
        totalWebpages: entry.total_webpages,
        successfullPages: entry.successfull_pages,
        imagesDetected: entry.images_detected,
        markdownSizeKb: entry.markdown_size_kb,
        documentsProcessed: entry.successful_documents,
        chunksGenerated: entry.chunks_generated,
    }));

    if (isLoading || !repository || Object.keys(repository).length === 0) {
        return <div>Loading...</div>;
    }

    if (errorMessage) {
        return <NoDataView text={errorMessage} icon={faGlobe} iconColor="sky" />;
    }

    return (
        <>
            <CustomTable
                tableLayout='fixed'
                columns={columns}
                dataSource={data}
                rowKey='key'
                pagination={{ defaultPageSize: 3 }}
                rowClassName={(record) =>
                    record?.key === selectedRowKey
                        ? 'bg-backgrounds-primary50 border-l-4 border-primary'
                        : ''
                }
                onRow={(record) => ({
                    onClick: () => {
                        if (onSelectRow) {
                            onSelectRow(record);
                        }
                    },
                })}
            />
        </>
    );
};
